import { string } from 'yup';

export const MIN_NAME_LENGTH = 2;
export const PTBR_PERSONAL_NAME_REGEX = /^[A-Za-zÀ-ÁÂÃÇÈÉÊÍÓÔÕÚàáâãçèéêíóôõú\s\-']+$/;
export const PTBR_BUSINESS_NAME_REGEX = /^[A-Za-zÀ-ÁÂÃÇÈÉÊÍÓÔÕÚàáâãçèéêíóôõú0-9\s&\-\/\.,]+$/;

export const collapseWhitespace = (value: string): string => {
	if (typeof value !== 'string') return '';
	return value.replace(/\s+/g, ' ').trim();
};

export const isNotOnlyWhitespace = (
	value: string | undefined | null,
): boolean => {
	if (value === undefined || value === null) return true;
	const normalized = collapseWhitespace(String(value));
	return normalized.length > 0;
};

export const hasMinLength = (value: string | undefined | null): boolean => {
	if (value === undefined || value === null) return true;
	return collapseWhitespace(String(value)).length >= MIN_NAME_LENGTH;
};

export const hasNoNumbers = (value: string | undefined | null): boolean => {
	if (value === undefined || value === null) return true;
	return !/\d/.test(String(value));
};

export const hasOnlyPtBrLetters = (
	value: string | undefined | null,
): boolean => {
	if (value === undefined || value === null) return true;
	return PTBR_PERSONAL_NAME_REGEX.test(String(value));
};

export const hasOnlyBusinessNameChars = (
    value: string | undefined | null,
): boolean => {
    if (value === undefined || value === null) return true;
    return PTBR_BUSINESS_NAME_REGEX.test(String(value));
};

export const createNameValidationRules = (
	isRequired = false,
	fieldName = 'nome',
) => {
	const baseRules = string()
		.transform((val) => collapseWhitespace(val ?? ''))
		.test(
			'not-only-spaces',
			'não pode conter apenas espaços',
			isNotOnlyWhitespace,
		)
		.test(
			'min-length',
			`deve ter pelo menos ${MIN_NAME_LENGTH} caracteres`,
			hasMinLength,
		)
		.test('no-numbers', 'não pode conter números', hasNoNumbers)
		.test(
			'no-special-chars',
			'não pode conter caracteres especiais',
			hasOnlyPtBrLetters,
		);

	return isRequired
		? baseRules.required(`${fieldName} é obrigatório.`)
		: baseRules.optional();
};

export const createBusinessNameValidationRules = (
	isRequired = false,
	fieldName = 'Razão social',
) => {
	const baseRules = string()
		.transform((val) => collapseWhitespace(val ?? ''))
		.test(
			'not-only-spaces',
			'não pode conter apenas espaços',
			isNotOnlyWhitespace,
		)
		.test(
			'min-length',
			`deve ter pelo menos ${MIN_NAME_LENGTH} caracteres`,
			hasMinLength,
		)
		.test(
			'only-drei-chars',
			'apenas letras, números, espaços e &, -, /, ., , são permitidos',
			hasOnlyBusinessNameChars,
		);

	return isRequired
		? baseRules.required(`${fieldName} é obrigatória.`)
		: baseRules.optional();
};

export const createFieldBlurHandler = <T extends Record<string, unknown>>(
	fieldName: 'firstName' | 'lastName',
	formikInstance: {
		handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
		values: T;
		setFieldValue: (field: keyof T, value: string, shouldValidate?: boolean) => void;
	},
) => {
	return (e: React.FocusEvent<HTMLInputElement>) => {
		formikInstance.handleBlur(e);
		const currentValue = formikInstance.values[fieldName] as string;
		const normalized = collapseWhitespace(currentValue);
		if (normalized !== currentValue) {
			formikInstance.setFieldValue(fieldName, normalized, true);
		}
	};
};

export const normalizeAndSplitFullName = (
	firstName: string | undefined,
	lastName: string | undefined,
	isPhysicalPerson = true,
) => {
	const normalizedFirst = collapseWhitespace(firstName || '');
	const normalizedLast = collapseWhitespace(lastName || '');

	let firstToSave = normalizedFirst;
	let lastToSave = normalizedLast;

	if (isPhysicalPerson && !lastToSave && normalizedFirst.includes(' ')) {
		const [first, ...rest] = normalizedFirst.split(' ');
		firstToSave = first;
		lastToSave = collapseWhitespace(rest.join(' '));
	}

	return { firstToSave, lastToSave };
};
